// Employee availability — per weekday: available toggle + from/to time.
// Premium redesigned view: hero card + one row per day with clean toggle + times.
import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader } from "@/components/ui-bits";
import { toast } from "sonner";
import { CalendarCheck, Clock, Check, X as XIcon } from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const emptyItem = () => ({ from_time: "09:00", to_time: "17:00", available: true, note: "" });

export default function MyAvailability() {
  const { t } = useI18n();
  const [items, setItems] = useState(DAYS.map((_, i) => ({ day_of_week: i, ...emptyItem() })));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/availability");
      if (data.length) {
        const mapped = DAYS.map((_, i) => data.find((d) => d.day_of_week === i) || { day_of_week: i, ...emptyItem() });
        setItems(mapped);
      }
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/availability", items.map(({ day_of_week, from_time, to_time, available, note }) => ({
        day_of_week, from_time, to_time, available, note,
      })));
      toast.success(t("availability_saved"));
    } finally {
      setBusy(false);
    }
  };

  const update = (i, k, v) => setItems((old) => old.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const availableCount = useMemo(() => items.filter((x) => x.available).length, [items]);

  return (
    <div data-testid="page-availability" className="pb-24">
      <PageHeader
        eyebrow={t("my_availability")}
        title={t("my_availability")}
        subtitle={t("availability_intro")}
        actions={
          <button onClick={save} disabled={busy} className="btn btn-primary" data-testid="avail-save">
            <Check size={14} strokeWidth={2.2} /> {busy ? "..." : t("save")}
          </button>
        }
      />

      {/* Hero summary — consistent with Team Vandaag / Aanvragen */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="card p-5 mb-4 flex items-center gap-4"
        style={{ background: "linear-gradient(135deg, hsl(var(--primary-soft)) 0%, hsl(var(--surface)) 60%)" }}
        data-testid="avail-hero"
      >
        <div
          className="w-12 h-12 rounded-2xl grid place-items-center shrink-0 text-white"
          style={{
            background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(217 91% 45%) 100%)",
            boxShadow: "0 12px 28px -10px hsl(var(--primary) / 0.55)",
          }}
        >
          <CalendarCheck size={20} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[22px] font-bold text-[hsl(var(--text-strong))] leading-none tabular-nums">
            {availableCount}<span className="text-[14px] text-[hsl(var(--text-muted))] font-normal">/7</span>
          </div>
          <div className="text-[12px] font-semibold text-[hsl(var(--text-muted))] mt-1">
            {availableCount === 1 ? "dag beschikbaar" : "dagen beschikbaar"}
          </div>
        </div>
      </motion.div>

      {/* Day rows */}
      <div className="flex flex-col gap-2">
        {DAYS.map((d, i) => {
          const active = items[i].available;
          return (
            <motion.div
              key={d}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }}
              className={`card p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 relative overflow-hidden ${!active ? "opacity-70" : ""}`}
              style={{ borderLeft: `4px solid ${active ? "hsl(var(--success))" : "hsl(var(--border))"}` }}
              data-testid={`avail-row-${d}`}
            >
              {/* Day name */}
              <div className="flex items-center justify-between sm:justify-start gap-3 sm:w-32">
                <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))] capitalize">{t(d)}</div>
              </div>

              {/* Times */}
              <div className="flex-1 flex items-center gap-2">
                <Clock size={12} className="text-[hsl(var(--text-muted))] shrink-0" strokeWidth={2} />
                <input
                  type="time"
                  data-testid={`avail-from-${d}`}
                  value={items[i].from_time}
                  onChange={(e) => update(i, "from_time", e.target.value)}
                  disabled={!active}
                  className="input-field !h-10 flex-1 !text-[13px] font-mono disabled:opacity-40"
                />
                <span className="text-[hsl(var(--text-muted))] text-sm">–</span>
                <input
                  type="time"
                  data-testid={`avail-to-${d}`}
                  value={items[i].to_time}
                  onChange={(e) => update(i, "to_time", e.target.value)}
                  disabled={!active}
                  className="input-field !h-10 flex-1 !text-[13px] font-mono disabled:opacity-40"
                />
              </div>

              {/* Toggle — icon-only tap target (checkmark = available, cross = not) */}
              <button
                onClick={() => update(i, "available", !active)}
                data-testid={`avail-toggle-${d}`}
                aria-label={active ? t("yes") : t("no")}
                className={`w-11 h-11 rounded-xl grid place-items-center border transition-colors shrink-0 ${
                  active
                    ? "bg-[hsl(var(--success-soft))] border-[hsl(var(--success)_/_0.3)] text-[hsl(var(--success))]"
                    : "bg-[hsl(var(--danger-soft))] border-[hsl(var(--danger)_/_0.3)] text-[hsl(var(--danger))]"
                }`}
              >
                {active ? <Check size={17} strokeWidth={2.6} /> : <XIcon size={17} strokeWidth={2.6} />}
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
